import { useState } from "react";
import { Application } from "@/types/application";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Copy, XCircle, CheckCircle, Trash2, History } from "lucide-react";
import { useAppConfigurations } from "@/hooks/useAppConfigurations";
import { AppConfigurationDialog } from "./AppConfigurationDialog";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ConfigVersionHistoryDialog } from "./ConfigVersionHistoryDialog";

interface AppConfigurationTabProps {
  application: Application;
}

export const AppConfigurationTab = ({ application }: AppConfigurationTabProps) => {
  const { configurations, isLoading, createConfiguration, updateConfiguration, deleteConfiguration, toggleConfiguration } = useAppConfigurations(application.id);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [deleteDialogConfig, setDeleteDialogConfig] = useState<any>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyConfigId, setHistoryConfigId] = useState<string>("");
  const [historyConfigName, setHistoryConfigName] = useState<string>("");

  const handleEdit = (config: any) => {
    setEditingConfig(config);
  };

  const handleDuplicate = async (config: any) => {
    await createConfiguration({
      applicationId: application.id,
      name: `${config.name} (Copy)`,
      description: config.description,
      configurationData: config.configurationData,
      customers: config.customers,
      isActive: false,
    });
  };

  const handleToggle = async (configId: string, currentState: boolean) => {
    await toggleConfiguration(configId, !currentState);
  };

  const handleDelete = (config: any) => {
    setDeleteDialogConfig(config);
  };

  const confirmDelete = async () => {
    if (deleteDialogConfig) {
      await deleteConfiguration(deleteDialogConfig.id);
      setDeleteDialogConfig(null);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-muted-foreground">Loading configurations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Manage configuration profiles for this application
        </p>
        <Button size="sm" onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Add Configuration
        </Button>
      </div>

      {configurations.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            No app configurations yet
          </p>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create First Configuration
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {configurations.map((config) => (
            <div key={config.id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium">{config.name}</h4>
                    {config.isActive && (
                      <Badge variant="default" className="text-xs">Active</Badge>
                    )}
                  </div>
                  {config.description && (
                    <p className="text-sm text-muted-foreground mb-3">
                      {config.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{config.configurationData.keys.length} keys</span>
                    <span>•</span>
                    <span>{config.customers.length} customers</span>
                  </div>
                </div>
                <ActionsDropdown
                  actions={[
                    {
                      label: "Version History",
                      icon: History,
                      onClick: () => {
                        setHistoryConfigId(config.id);
                        setHistoryConfigName(config.name);
                        setHistoryDialogOpen(true);
                      }
                    },
                    {
                      label: "Edit",
                      icon: Edit,
                      onClick: () => handleEdit(config)
                    },
                    {
                      label: "Duplicate",
                      icon: Copy,
                      onClick: () => handleDuplicate(config)
                    },
                    {
                      label: config.isActive ? "Deactivate" : "Activate",
                      icon: config.isActive ? XCircle : CheckCircle,
                      onClick: () => handleToggle(config.id, config.isActive)
                    },
                    {
                      label: "Delete",
                      icon: Trash2,
                      onClick: () => handleDelete(config),
                      destructive: true
                    }
                  ]}
                />
              </div>

              {/* Preview of keys */}
              <div className="mt-4 pt-4 border-t">
                <div className="space-y-2">
                  {config.configurationData.keys.slice(0, 3).map((kvp, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs bg-muted/50 rounded p-2">
                      <span className="font-mono text-muted-foreground">{kvp.key}</span>
                      <span className="text-muted-foreground truncate max-w-[200px]">
                        {String(kvp.value)}
                      </span>
                    </div>
                  ))}
                  {config.configurationData.keys.length > 3 && (
                    <p className="text-xs text-muted-foreground">
                      +{config.configurationData.keys.length - 3} more keys
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AppConfigurationDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSave={createConfiguration}
        applicationId={application.id}
      />

      {editingConfig && (
        <AppConfigurationDialog
          open={true}
          onOpenChange={(open) => !open && setEditingConfig(null)}
          onSave={(config) => updateConfiguration(editingConfig.id, config)}
          applicationId={application.id}
          editConfig={editingConfig}
        />
      )}

      <AlertDialog open={!!deleteDialogConfig} onOpenChange={(open) => !open && setDeleteDialogConfig(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialogConfig?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfigVersionHistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        configurationId={historyConfigId}
        configurationName={historyConfigName}
      />
    </div>
  );
};
